/* ==========================================================================
   ProGym Chronicles — theme.js
   Progressive enhancement. GSAP + ScrollTrigger are loaded in <head>.
   Everything degrades gracefully and respects prefers-reduced-motion.
   ========================================================================== */
(function () {
  'use strict';
  var docEl = document.documentElement;
  docEl.classList.remove('no-js');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasGSAP = typeof window.gsap !== 'undefined';
  if (hasGSAP && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  /* ---------------- Loader ---------------- */
  function runLoader() {
    var loader = document.querySelector('[data-loader]');
    if (!loader) { document.body.classList.remove('is-loading'); startEntrance(); return; }
    var pct = loader.querySelector('[data-loader-pct]');
    var fill = loader.querySelector('.loader__fill');
    var words = loader.querySelectorAll('.loader__word span');
    if (REDUCED || !hasGSAP) {
      document.body.classList.remove('is-loading');
      loader.style.display = 'none';
      startEntrance();
      return;
    }
    var counter = { v: 0 };
    var tl = gsap.timeline({ onComplete: function () {
      loader.style.display = 'none';
      document.body.classList.remove('is-loading');
      startEntrance();
    }});
    gsap.set(words, { yPercent: 110 });
    tl.to(words, { yPercent: 0, duration: .9, stagger: .06, ease: 'power4.out' })
      .to(counter, { v: 100, duration: 1.4, ease: 'power1.inOut', onUpdate: function () {
        if (pct) pct.textContent = String(Math.round(counter.v)).padStart(3, '0');
      }}, .1)
      .to(fill, { scaleX: 1, duration: 1.4, ease: 'power1.inOut' }, .1)
      .to(loader, { yPercent: -100, duration: 1, ease: 'power4.inOut' }, '+=.15');
  }

  /* ---------------- Entrance (hero) ---------------- */
  function startEntrance() {
    splitLines();
    var heroLines = document.querySelectorAll('.hero__title .line > span, [data-hero-reveal] .line > span');
    if (hasGSAP && !REDUCED && heroLines.length) {
      gsap.set(heroLines, { yPercent: 110 });
      gsap.to(heroLines, { yPercent: 0, duration: 1.1, stagger: .08, ease: 'power4.out', delay: .1 });
      gsap.from('[data-hero-fade]', { opacity: 0, y: 24, duration: 1, stagger: .12, delay: .5, ease: 'power3.out' });
    }
  }

  /* ---------------- Split text into lines ---------------- */
  function splitLines() {
    document.querySelectorAll('[data-split-lines]').forEach(function (el) {
      if (el.dataset.splitDone) return;
      var text = el.textContent.trim();
      el.dataset.splitDone = '1';
      // naive line split by word wrapping: wrap each word, group later via layout
      el.innerHTML = '';
      var words = text.split(/\s+/);
      var lineWrap = document.createElement('span');
      lineWrap.className = 'line';
      words.forEach(function (w, i) {
        lineWrap.appendChild(document.createTextNode(w + (i < words.length - 1 ? ' ' : '')));
      });
      var inner = document.createElement('span');
      inner.appendChild(document.createTextNode(text));
      lineWrap.innerHTML = '';
      lineWrap.appendChild(inner);
      el.appendChild(lineWrap);
    });
  }

  /* ---------------- Custom cursor ---------------- */
  function initCursor() {
    if (window.matchMedia('(hover:none)').matches) return;
    var dot = document.querySelector('.cursor__dot');
    var ring = document.querySelector('.cursor__ring');
    if (!dot || !ring) return;
    var mx = window.innerWidth / 2, my = window.innerHeight / 2, rx = mx, ry = my;
    window.addEventListener('mousemove', function (e) {
      mx = e.clientX; my = e.clientY;
      dot.style.transform = 'translate(' + mx + 'px,' + my + 'px) translate(-50%,-50%)';
    });
    function loop() {
      rx += (mx - rx) * 0.18; ry += (my - ry) * 0.18;
      ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px) translate(-50%,-50%)';
      requestAnimationFrame(loop);
    }
    loop();
    document.querySelectorAll('a,button,[data-cursor="hover"],.card,.doc__play,.compare').forEach(function (el) {
      el.addEventListener('mouseenter', function () { ring.classList.add('is-hover'); });
      el.addEventListener('mouseleave', function () { ring.classList.remove('is-hover'); });
    });
    document.addEventListener('mouseleave', function () { ring.classList.add('is-hidden'); dot.style.opacity = 0; });
    document.addEventListener('mouseenter', function () { ring.classList.remove('is-hidden'); dot.style.opacity = 1; });
  }

  /* ---------------- Header (hide on scroll down, solid over light) ---------------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var last = 0, threshold = 10;
    var overHero = header.dataset.overlay === 'true';
    function update() {
      var y = window.scrollY;
      if (overHero) { header.classList.toggle('site-header--solid', y > window.innerHeight * 0.7); }
      else { header.classList.add('site-header--solid'); }
      if (y > last && y > 200) header.classList.add('is-hidden');
      else header.classList.remove('is-hidden');
      last = y;
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ---------------- Mobile nav ---------------- */
  function initMobileNav() {
    var toggle = document.querySelector('[data-nav-toggle]');
    var menu = document.querySelector('[data-mobile-nav]');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', function () {
      var open = menu.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
    });
    menu.querySelectorAll('a').forEach(function (a) {
      a.addEventListener('click', function () {
        menu.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      });
    });
  }

  /* ---------------- Scroll reveals ---------------- */
  function initReveals() {
    var els = document.querySelectorAll('.reveal, .reveal-stagger');
    if (!els.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    if (hasGSAP && window.ScrollTrigger) {
      document.querySelectorAll('.reveal').forEach(function (el) {
        gsap.fromTo(el, { autoAlpha: 0, y: 30 }, {
          autoAlpha: 1, y: 0, duration: 1, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 85%' }
        });
      });
      document.querySelectorAll('.reveal-stagger').forEach(function (group) {
        gsap.fromTo(group.children, { autoAlpha: 0, y: 26 }, {
          autoAlpha: 1, y: 0, duration: .9, stagger: .1, ease: 'power3.out',
          scrollTrigger: { trigger: group, start: 'top 82%' }
        });
      });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
      }, { threshold: 0.15 });
      els.forEach(function (el) { io.observe(el); });
    }
  }

  /* ---------------- Parallax media ---------------- */
  function initParallax() {
    if (!hasGSAP || !window.ScrollTrigger || REDUCED) return;
    document.querySelectorAll('[data-parallax]').forEach(function (el) {
      var amt = parseFloat(el.dataset.parallax) || 14;
      gsap.fromTo(el, { yPercent: -amt / 2 }, {
        yPercent: amt / 2, ease: 'none',
        scrollTrigger: { trigger: el.closest('[data-parallax-scope]') || el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  }

  /* ---------------- Pinned storytelling ---------------- */
  function initPinned() {
    if (!hasGSAP || !window.ScrollTrigger || REDUCED) return;
    document.querySelectorAll('[data-zoom-scroll]').forEach(function (el) {
      gsap.fromTo(el.querySelector('img'), { scale: 1.18 }, {
        scale: 1, ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  }

  /* ---------------- Marquee ---------------- */
  function initMarquee() {
    document.querySelectorAll('[data-marquee]').forEach(function (track) {
      var content = track.innerHTML;
      track.innerHTML = content + content;
      if (!hasGSAP || REDUCED) return;
      var speed = parseFloat(track.dataset.marquee) || 40;
      var dir = track.dataset.marqueeDir === 'right' ? 1 : -1;
      gsap.to(track, { xPercent: dir * -50, duration: speed, ease: 'none', repeat: -1 });
    });
  }

  /* ---------------- Before / after compare ---------------- */
  function initCompare() {
    document.querySelectorAll('[data-compare]').forEach(function (el) {
      var after = el.querySelector('.compare__after');
      var handle = el.querySelector('.compare__handle');
      var dragging = false;
      function setPos(clientX) {
        var rect = el.getBoundingClientRect();
        var p = ((clientX - rect.left) / rect.width) * 100;
        p = Math.max(0, Math.min(100, p));
        el.style.setProperty('--pos', p + '%');
      }
      function down(e) { dragging = true; setPos((e.touches ? e.touches[0] : e).clientX); }
      function move(e) { if (dragging) setPos((e.touches ? e.touches[0] : e).clientX); }
      function up() { dragging = false; }
      el.addEventListener('mousedown', down); el.addEventListener('touchstart', down, { passive: true });
      window.addEventListener('mousemove', move); window.addEventListener('touchmove', move, { passive: true });
      window.addEventListener('mouseup', up); window.addEventListener('touchend', up);
    });
  }

  /* ---------------- Reading progress ---------------- */
  function initReadingProgress() {
    var rail = document.querySelector('[data-progress]');
    var article = document.querySelector('[data-article-body]');
    if (!rail || !article) return;
    var fill = rail.querySelector('.progress-rail__fill');
    function update() {
      var rect = article.getBoundingClientRect();
      var total = article.offsetHeight - window.innerHeight;
      var scrolled = Math.min(Math.max(-rect.top, 0), total);
      var p = total > 0 ? scrolled / total : 0;
      fill.style.transform = 'scaleX(' + p + ')';
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  /* ---------------- Video / documentary play ---------------- */
  function initVideo() {
    document.querySelectorAll('[data-video-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('[data-video-scope]');
        var src = btn.dataset.videoSrc;
        if (!wrap || !src) return;
        var frame = document.createElement('iframe');
        frame.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
        frame.allow = 'autoplay; fullscreen; picture-in-picture';
        frame.setAttribute('allowfullscreen', '');
        frame.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;z-index:5';
        frame.title = btn.getAttribute('aria-label') || 'Video';
        wrap.appendChild(frame);
        btn.style.display = 'none';
      });
    });
  }

  /* ---------------- Predictive search (lightweight) ---------------- */
  function initSearch() {
    var trigger = document.querySelector('[data-search-trigger]');
    var overlay = document.querySelector('[data-search-overlay]');
    if (!trigger || !overlay) return;
    var input = overlay.querySelector('input[type="search"]');
    var results = overlay.querySelector('[data-search-results]');
    trigger.addEventListener('click', function () {
      overlay.classList.add('is-open'); document.body.style.overflow = 'hidden';
      setTimeout(function () { input && input.focus(); }, 300);
    });
    overlay.querySelectorAll('[data-search-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    function close() { overlay.classList.remove('is-open'); document.body.style.overflow = ''; }
    if (!input || !results) return;
    var t;
    input.addEventListener('input', function () {
      clearTimeout(t);
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      t = setTimeout(function () {
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=article,product,page&resources[limit]=6')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var res = data.resources.results;
            var items = [].concat(res.articles || [], res.products || [], res.pages || []);
            results.innerHTML = items.length
              ? items.map(function (i) { return '<a href="' + i.url + '" class="search-result"><span>' + i.title + '</span></a>'; }).join('')
              : '<p class="search-empty">No stories yet. Try another word.</p>';
          }).catch(function () {});
      }, 220);
    });
  }

  /* ---------------- Cart count (fetch) ---------------- */
  function refreshCartCount() {
    var el = document.querySelector('[data-cart-count]');
    if (!el) return;
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      el.textContent = c.item_count;
      el.style.display = c.item_count > 0 ? '' : 'none';
    }).catch(function () {});
  }

  /* ---------------- Init ---------------- */
  function init() {
    initCursor();
    initHeader();
    initMobileNav();
    initReveals();
    initParallax();
    initPinned();
    initMarquee();
    initCompare();
    initReadingProgress();
    initVideo();
    initSearch();
    refreshCartCount();
    if (hasGSAP && window.ScrollTrigger) ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { runLoader(); init(); });
  } else { runLoader(); init(); }
  window.addEventListener('load', function () { if (hasGSAP && window.ScrollTrigger) ScrollTrigger.refresh(); });
})();
